import React, { useEffect, useState } from 'react'
import { NavLink as RouterNavLink, useLocation, matchPath } from 'react-router'
import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import { alpha, type Theme } from '@mui/material/styles'

function doesPathBelongTo(path: string, parentPath: string): boolean {
    return !!matchPath({ path: parentPath, end: false }, path)
}

const SiblingUrlsContext = React.createContext<string[]>([])
/** How many collapsible groups enclose an item; groups nested inside groups indent their items further. */
const NestingDepthContext = React.createContext(0)
const nestedIndentPx = 12

const navLinkStyle = { display: 'block', outline: 'none', textDecoration: 'none', color: 'inherit' } as const

/** Shared base styles for navigation and action rows inside a sidebar. */
export const sidebarRowSx = (theme: Theme) => ({
    position: 'relative',
    width: `calc(100% - ${theme.spacing(2)})`,
    margin: theme.spacing(0.25, 1),
    padding: theme.spacing(1, 1.5) + ' !important', // FIXME: Remove important
    boxSizing: 'border-box',
    borderRadius: '8px',
    flexGrow: 0,
})

function listItemSx(active?: boolean, clickable = true) {
    return (theme: Theme) => ({
        ...sidebarRowSx(theme),
        cursor: clickable ? 'pointer' : 'default',
        ...(active !== undefined && {
            transition: theme.transitions.create('background-color'),
            // NOTE: MUI sx doesn't support comma-separated selectors like '&:hover, &:focus-within'
            '&:hover': {
                backgroundColor: active
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.common.white, 0.15),
            },
            '&:focus-within': {
                backgroundColor: active
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.common.white, 0.15),
            },
            ...(active && {
                backgroundColor: alpha(theme.palette.primary.main, 0.14),
                color: theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.primary.contrastText,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 7,
                    bottom: 7,
                    left: 0,
                    width: 3,
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: theme.palette.primary.main,
                },
            }),
        }),
    })
}

export interface SidebarMenuItemProps {
    id?: string
    children?: Array<SidebarMenuItemProps | null>
    icon?: React.ReactElement
    url?: string
    target?: React.ComponentProps<typeof RouterNavLink>['target']
    name: React.ReactElement | string
    iconColor?: string | false
    divider?: boolean
    onClick?: () => void
    secondaryText?: React.ReactElement | string
    /** Optional element rendered after the text (e.g. a status chip) */
    trailing?: React.ReactNode
    /** Explicit selection for action rows that do not navigate to another URL. */
    selected?: boolean
    /** When true, the item never shows the active highlight (e.g. a profile shortcut whose url overlaps a nav item) */
    disableActiveHighlight?: boolean
    /** When true, the item is active only on its own url, not on the pages below it (e.g. a home link at `/`) */
    end?: boolean
    /** When true, long names wrap onto several lines instead of being truncated */
    wrap?: boolean
}

/**
 * Sidebar menu item
 * @param name - text in it
 * @param url - url to lead to, including cached urlParams
 * @param icon - icon to show
 * @param children - array of subItems. Can't be used with `onClick`
 * @param iconColor - if wee need to set other color for the icon
 * @param onClick - if we need to call some action instead of leading somewhere. Can't be used with `children`
 * @param id
 * @param secondaryText - text to show under the name
 */
export const SidebarMenuItem = ({
    name,
    url,
    target,
    icon,
    children,
    iconColor,
    onClick,
    id,
    secondaryText,
    trailing,
    selected,
    disableActiveHighlight,
    end,
    wrap,
}: SidebarMenuItemProps) => {
    const location = useLocation()
    const currentPath = location.pathname
    const siblingUrls = React.useContext(SiblingUrlsContext)
    const depth = React.useContext(NestingDepthContext)
    const childUrls = React.useMemo(
        () => children?.filter((c): c is SidebarMenuItemProps => !!c?.url).map((c) => c.url!) ?? [],
        [children]
    )
    const shouldBeExpanded =
        children?.some((child) => {
            if (child?.url) {
                const element = document.createElement('a')
                element.href = child.url
                const menuItemPath = element.pathname
                return doesPathBelongTo(currentPath, menuItemPath)
            }
            return false
        }) || false

    const [expanded, setExpanded] = useState(shouldBeExpanded)

    useEffect(() => {
        setExpanded(shouldBeExpanded)
    }, [currentPath, shouldBeExpanded])

    const handleOnClick = children ? () => setExpanded(!expanded) : onClick
    const isClickable = !!url || !!onClick || !!children
    const leftPadding = '34px'
    const nestedIndent = Math.max(depth - 1, 0) * nestedIndentPx
    const textIndent = icon ? 0 : nestedIndent ? `calc(${leftPadding} + ${nestedIndent}px)` : leftPadding
    const renderListItem = (active?: boolean) => (
        <>
            <ListItem
                dense
                onClick={handleOnClick}
                {...(!url && handleOnClick
                    ? {
                          role: 'button',
                          tabIndex: 0,
                          'aria-pressed': selected,
                          onKeyDown: (event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  handleOnClick()
                              }
                          },
                      }
                    : {})}
                sx={listItemSx(selected ?? active, isClickable)}
                id={id}
                component="div"
            >
                {!!icon && (
                    <ListItemIcon sx={{ minWidth: leftPadding, color: iconColor || 'inherit' }}>{icon}</ListItemIcon>
                )}
                <ListItemText
                    sx={{ paddingLeft: textIndent }}
                    primary={name}
                    secondary={secondaryText}
                    slotProps={{
                        primary: {
                            noWrap: !wrap,
                            title: !wrap && typeof name === 'string' ? name : undefined,
                        },
                        secondary: { component: 'div' },
                    }}
                />
                {trailing}
                {!!children && (expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}
            </ListItem>
        </>
    )

    return url ? (
        <RouterNavLink
            to={url}
            end={end}
            style={navLinkStyle}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        >
            {({ isActive }) => {
                if (disableActiveHighlight) return renderListItem(false)
                if (isActive) {
                    const linkUrl = new URL(url, window.location.origin)
                    // NavLink uses prefix matching: if our pathname doesn't exactly match
                    // and a sibling has a more specific match, we should not be active.
                    if (linkUrl.pathname !== location.pathname && siblingUrls.length > 0) {
                        const hasMoreSpecificSibling = siblingUrls.some((sibUrl) => {
                            if (sibUrl === url) return false
                            const sibPathname = new URL(sibUrl, window.location.origin).pathname
                            return (
                                sibPathname.length > linkUrl.pathname.length &&
                                doesPathBelongTo(location.pathname, sibPathname)
                            )
                        })
                        if (hasMoreSpecificSibling) isActive = false
                    }
                    // NavLink only matches pathname; also verify search params when present
                    if (isActive && linkUrl.search) {
                        const linkParams = new URLSearchParams(linkUrl.search)
                        const currentParams = new URLSearchParams(location.search)
                        isActive = [...linkParams].every(([key, value]) => currentParams.get(key) === value)
                    }
                }
                return renderListItem(isActive)
            }}
        </RouterNavLink>
    ) : (
        <>
            {renderListItem(onClick ? false : undefined)}
            {!!children && (
                <SiblingUrlsContext.Provider value={childUrls}>
                    <NestingDepthContext.Provider value={depth + 1}>
                        <Collapse in={expanded} data-testid={`${name}-sub`}>
                            {children.map((child, ind) => {
                                const key = child?.url || `${name}-${ind}`
                                return (
                                    !!child && (
                                        <React.Fragment key={key}>
                                            <SidebarMenuItem {...child} />
                                            {child.divider && <Divider variant="middle" />}
                                        </React.Fragment>
                                    )
                                )
                            })}
                        </Collapse>
                    </NestingDepthContext.Provider>
                </SiblingUrlsContext.Provider>
            )}
        </>
    )
}
